<template>
  <div class="grid grid-cols-2 gap-x-[26px]">
    <div>
      <div
        class="w-full h-[56px] bg-gray-300/30 rounded-t-xl px-[15px] py-[9px]"
      >
        <div
          class="font-secondary font-medium text-[12px] leading-[18px] px-[5px] py-px bg-gray-200 text-gray-800 rounded-md w-fit mb-[3px]"
        >
          Registered work
        </div>

        <div class="font-secondary text-[13px] leading-[18px] text-gray-800">
          {{ match.copyright.location }}
        </div>
      </div>

      <div class="border-2 border-t-0 border-gray-300/30 px-8 py-6">
        <div class="mb-[25px]">
          <div
            class="font-secondary text-[12px] leading-[16px] text-gray-500 mb-0.5"
          >
            Registration
          </div>

          <div class="flex gap-x-1.5 mb-[25px]">
            <ThumbnailIcon
              class="w-5 h-5 rounded-xs bg-gray-200/25 p-px"
              :icon-size="16"
              :image-url="copyrightFile.asset?.previewImageUrl"
              :type="copyrightFile.type"
            />

            <div class="min-w-0 break-all">
              <div
                class="font-secondary font-medium text-[13px] leading-[20px] text-gray-800 mb-[3px]"
              >
                {{ copyrightFile.name }}
              </div>

              <div
                v-if="showOriginalFilename"
                class="font-secondary text-[12px] leading-[18px] text-gray-800 mb-[3px]"
              >
                Filename:
                {{ copyrightFile.metadata.originalFilename }}
              </div>

              <RouterLink
                :to="`/file/${copyrightFile.copyright.id}`"
                class="font-primary text-[12px] leading-[18px] text-link"
              >
                View registration
              </RouterLink>
            </div>
          </div>

          <component
            :is="component"
            :match="match.copyright"
            :number="number"
            side="registered"
            :allow-playback="canPlayMatches"
          />
        </div>
      </div>
    </div>

    <div>
      <div
        class="w-full h-[56px] rounded-t-xl px-[15px] py-[9px]"
        :class="bgClass"
      >
        <div class="flex items-center justify-between">
          <div
            class="font-secondary font-medium text-[12px] leading-[18px] px-[5px] py-px text-white rounded-md w-fit mb-[3px]"
            :class="pillClasses"
          >
            Potential infringement {{ number }}
          </div>

          <div class="flex items-center gap-x-3">
            <div
              class="font-secondary font-medium text-[12px] leading-[18px] text-white"
            >
              {{ match.infringingFile.score }}% match
            </div>

            <div class="w-[100px] h-2 bg-white rounded-md p-px">
              <div
                class="h-full rounded-md"
                :class="bgClass"
                :style="{
                  width: `${Number(match.infringingFile.score)}%`,
                }"
              ></div>
            </div>
          </div>
        </div>

        <div class="font-secondary text-[13px] leading-[18px] text-white">
          {{ match.infringingFile.location }}
        </div>
      </div>

      <div
        class="border-2 rounded-xl rounded-t-none border-t-0 px-8 py-6"
        :class="borderClass"
      >
        <div class="flex gap-x-3 mb-[25px]">
          <Icon
            name="Alert"
            :size="26"
            class="mt-0.5"
            :class="{
              'stroke-red-500': match.infringingFile.level === ScoreLevel.High,
              'stroke-yellow-500':
                match.infringingFile.level === ScoreLevel.Medium,
              'stroke-green-500': match.infringingFile.level === ScoreLevel.Low,
            }"
          />
          <div class="font-secondary font-medium text-[24px] leading-[30px]">
            {{ match.infringingFile.title }}
          </div>
        </div>

        <div class="mb-[25px]">
          <div
            class="font-secondary text-[12px] leading-[16px] text-gray-500 mb-0.5"
          >
            Potential infringing work
          </div>

          <div class="flex items-center gap-x-1.5">
            <ThumbnailIcon
              class="w-5 h-5 rounded-xs bg-gray-200/25 p-px"
              :icon-size="16"
              :image-url="infringingFile.asset.previewImageUrl"
              :type="infringingFile.type"
            />
            <div class="w-full" v-truncate>
              <div
                class="font-secondary text-[12px] leading-[18px] text-gray-800"
                data-truncate-target
              >
                {{ infringingFile.name }}
              </div>
            </div>
          </div>
        </div>

        <div class="mb-[25px]">
          <div
            class="font-secondary text-[12px] leading-[16px] text-gray-500 mb-1.5"
          >
            Infringement findings
          </div>

          <div class="font-secondary text-[14px] leaidng-[20px]">
            {{ match.infringingFile.summary }}
          </div>
        </div>

        <div class="w-full h-px my-[25px] bg-gray-200/50"></div>

        <component
          :is="component"
          :match="match.infringingFile"
          :number="number"
          side="infringing"
          :allow-playback="canPlayMatches"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { graphql } from '@/shared/graphql/router'

export const MatchBreakdownFragment = graphql(`
  fragment MatchBreakdown on MatchCategoryBreakdown {
    copyright {
      id
      location
      markers {
        color
        shine
        visible
      }
      file {
        ... on File {
          id
          name
          type
          asset {
            previewImageUrl
          }
          metadata {
            originalFilename
          }
          copyright {
            id
          }
        }
      }
      ... on AudioMatch {
        ...AudioMatchFields
      }
      ... on ImageMatch {
        ...ImageMatchFields
      }
      ... on TextMatch {
        ...TextMatchFields
      }
      ... on TranscriptMatch {
        ...TranscriptMatchFields
      }
      ... on VideoMatch {
        ...VideoMatchFields
      }
    }
    infringingFile {
      id
      location
      summary
      title
      score
      level
      markers {
        color
        shine
        visible
      }
      file {
        ... on InfringingFile {
          id
          name
          type
          asset {
            previewImageUrl
          }
        }
      }
      ... on AudioMatch {
        ...AudioMatchFields
      }
      ... on ImageMatch {
        ...ImageMatchFields
      }
      ... on TextMatch {
        ...TextMatchFields
      }
      ... on TranscriptMatch {
        ...TranscriptMatchFields
      }
      ... on VideoMatch {
        ...VideoMatchFields
      }
    }
  }
`)
</script>

<script setup lang="ts">
import {
  type FragmentType,
  useFragment,
} from '@/shared/graphql/router/fragment-masking'
import {
  MatchCategoryType,
  ScoreLevel,
  type File,
  type InfringingFile,
} from '@/shared/graphql/router/graphql'
import { Icon, ThumbnailIcon } from '@dacr/ui'
import { computed, type Component } from 'vue'
import AudioMatchGroup from './AudioMatchGroup.vue'
import TranscriptMatch from './TranscriptMatch.vue'
import ImageMatch from './ImageMatch.vue'
import TextMatchGroup from './TextMatchGroup.vue'
import VideoMatch from './VideoMatch.vue'

const props = defineProps<{
  type: MatchCategoryType
  match: FragmentType<typeof MatchBreakdownFragment>
  number: number
  allowPlayback?: boolean
}>()

const match = useFragment(MatchBreakdownFragment, props.match)

const canPlayMatches = computed(() => props.allowPlayback !== false)

const copyrightFile = computed(() => match.copyright.file as File)
const infringingFile = computed(
  () => match.infringingFile.file as InfringingFile
)

const component = computed<Component>(() => {
  if (props.type === MatchCategoryType.Audio) {
    return AudioMatchGroup
  }

  if (props.type === MatchCategoryType.Transcript) {
    return TranscriptMatch
  }

  if (props.type === MatchCategoryType.Text) {
    return TextMatchGroup
  }

  if (props.type === MatchCategoryType.Video) {
    return VideoMatch
  }

  return ImageMatch
})

const showOriginalFilename = computed(
  () =>
    copyrightFile.value.name !== copyrightFile.value.metadata.originalFilename
)

const pillClasses = computed(() => ({
  'bg-green-600': match.infringingFile.level === ScoreLevel.Low,
  'bg-yellow-600': match.infringingFile.level === ScoreLevel.Medium,
  'bg-red-600': match.infringingFile.level === ScoreLevel.High,
}))

const borderClass = computed(() => ({
  'border-green-500': match.infringingFile.level === ScoreLevel.Low,
  'border-yellow-500': match.infringingFile.level === ScoreLevel.Medium,
  'border-red-500': match.infringingFile.level === ScoreLevel.High,
}))

const bgClass = computed(() => ({
  'bg-green-500': match.infringingFile.level === ScoreLevel.Low,
  'bg-yellow-500': match.infringingFile.level === ScoreLevel.Medium,
  'bg-red-500': match.infringingFile.level === ScoreLevel.High,
}))
</script>
